import React, { Component } from 'react';
import { motion } from 'framer-motion';
import StarStory from '../components/StarStory';
import AnimatedCounter from '../components/AnimatedCounter';
const AffirmPage = () => {
  return <div className="bg-white min-h-screen" data-id="element-106">
      <div className="container mx-auto px-4 py-16" data-id="element-107">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row items-center md:items-start gap-6 mb-12" data-id="element-108">
          <img src="https://logo.clearbit.com/affirm.com" alt="Affirm logo" className="w-24 h-24 object-contain" data-id="element-109" />
          <div data-id="element-110">
            <motion.h1 className="text-4xl font-bold text-gray-800 mb-2" initial={{
            opacity: 0.5
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.5
          }} data-id="element-111">
              Head of Growth Product at Affirm
            </motion.h1>
            <motion.p className="text-xl text-gray-700 mb-4" initial={{
            opacity: 0.5
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} data-id="element-112">
              2021 — 2023
            </motion.p>
            <motion.div className="bg-gray-100 p-4 rounded-lg" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.5,
            delay: 0.4
          }} data-id="element-113">
              <p className="text-gray-700" data-id="element-114">
                <span className="font-bold" data-id="element-115">Context:</span> Public fintech.
                Revenue grew $300M → $1.2B/year.
              </p>
            </motion.div>
          </div>
        </div>
        {/* STAR Stories */}
        <StarStory title="Merchant Self-Service Scale" situation="Affirm's merchant onboarding process was manual, slow, and capped. Growth was limited because every new merchant required hands-on sales/ops support. Self-service was almost nonexistent." task="I needed to unlock a scalable model—bringing in thousands of merchants without scaling headcount linearly." action="I led the development of a merchant self-service portal. We automated underwriting and approvals, layered in self-configurable pricing plans, and built guided onboarding flows. I partnered with engineering to handle fraud checks via API integrations and worked with marketing to funnel prospects directly into this portal. We launched gradually, A/B testing every step of the funnel." result="Within 12 months, merchant count scaled from 0 to 160,000+ active merchants, generating over $1B in GMV. What was once bottlenecked by ops became a repeatable PLG engine. This initiative is still cited internally as the turning point in Affirm's merchant growth model." imageUrl="https://via.placeholder.com/600x400?text=Merchant+Dashboard+UI" imageAlt="Merchant dashboard UI with 160K+ merchants onboarded overlay" animationComponent={<div className="bg-white p-6 rounded-lg shadow-md" data-id="element-117">
              <h4 className="text-lg font-bold mb-4 text-center" data-id="element-118">
                Merchant Growth
              </h4>
              <div className="flex justify-between items-center mb-2" data-id="element-119">
                <span className="text-gray-700" data-id="element-120">Merchants:</span>
                <AnimatedCounter start={0} end={160000} suffix="+" data-id="element-121" />
              </div>
              <div className="flex justify-between items-center" data-id="element-122">
                <span className="text-gray-700" data-id="element-123">GMV:</span>
                <AnimatedCounter start={0} end={1} prefix="$" suffix="B" data-id="element-124" />
              </div>
            </div>} data-id="element-116" />
        <StarStory title="Card Business Growth" situation="Affirm's card product struggled with low activation rates. Despite broad distribution, only 12% of issued cards were activated, and usage lagged." task="My job was to triple activation rates and turn the card into a meaningful revenue line." action="We rebuilt the onboarding funnel with fewer friction points (reduced from 7 steps → 3). I worked with lifecycle marketing to introduce tailored nudges at day 3, 7, and 14, encouraging activation. We also streamlined card management in-app and launched instant digital card provisioning. Finally, I introduced referral bonuses where existing users could earn $10 when friends activated." result="Activation climbed from 12% → 37% within a year. GMV more than doubled from $400M → $800M, making the card business one of Affirm's fastest-growing lines." imageUrl="https://via.placeholder.com/600x400?text=Affirm+Card+Product" imageAlt="Card product image with app screenshot" animationComponent={<div className="bg-white p-6 rounded-lg shadow-md" data-id="element-126">
              <h4 className="text-lg font-bold mb-4 text-center" data-id="element-127">
                Card Business Growth
              </h4>
              <div className="flex justify-between items-center mb-2" data-id="element-128">
                <span className="text-gray-700" data-id="element-129">Activation Rate:</span>
                <div className="flex items-center" data-id="element-130">
                  <span className="text-gray-500 mr-2" data-id="element-131">12%</span>
                  <span className="text-gray-700 mx-2" data-id="element-132">→</span>
                  <AnimatedCounter start={12} end={37} suffix="%" data-id="element-133" />
                </div>
              </div>
              <div className="flex justify-between items-center" data-id="element-134">
                <span className="text-gray-700" data-id="element-135">GMV:</span>
                <div className="flex items-center" data-id="element-136">
                  <span className="text-gray-500 mr-2" data-id="element-137">$400M</span>
                  <span className="text-gray-700 mx-2" data-id="element-138">→</span>
                  <AnimatedCounter start={400} end={800} prefix="$" suffix="M" data-id="element-139" />
                </div>
              </div>
            </div>} data-id="element-125" />
        <StarStory title="Checkout Flow Revamp" situation="Affirm's checkout suffered from underwriting delays, leading to drop-offs. Funnel completion rate sat at 62%." task="As product lead, I had to rebuild checkout speed and reliability to unlock revenue." action="We refactored underwriting APIs, shaving 1.8s from load times. I led integration of install ads to encourage app downloads directly from checkout. We also A/B tested APR structures, offering more attractive 0% promotions. Finally, I collaborated with analytics to measure impact at each funnel stage." result="Checkout completion rose from 62% → 74%, a +12 point lift, translating into +$400M incremental GMV annually." imageUrl="https://via.placeholder.com/600x400?text=Affirm+Checkout+UI" imageAlt="Affirm checkout UI (product page → checkout overlay)" animationComponent={<div className="bg-white p-6 rounded-lg shadow-md" data-id="element-141">
              <h4 className="text-lg font-bold mb-4 text-center" data-id="element-142">
                Checkout Performance
              </h4>
              <div className="flex justify-between items-center mb-2" data-id="element-143">
                <span className="text-gray-700" data-id="element-144">Load Time:</span>
                <div className="flex items-center" data-id="element-145">
                  <span className="text-gray-500 mr-2" data-id="element-146">2.9s</span>
                  <span className="text-gray-700 mx-2" data-id="element-147">→</span>
                  <span className="text-[#0FB9B1] font-bold" data-id="element-148">1.1s</span>
                </div>
              </div>
              <div className="flex justify-between items-center" data-id="element-149">
                <span className="text-gray-700" data-id="element-150">Completion Rate:</span>
                <div className="flex items-center" data-id="element-151">
                  <span className="text-gray-500 mr-2" data-id="element-152">62%</span>
                  <span className="text-gray-700 mx-2" data-id="element-153">→</span>
                  <AnimatedCounter start={62} end={74} suffix="%" data-id="element-154" />
                </div>
              </div>
            </div>} data-id="element-140" />
        {/* Testimonial */}
        <motion.div className="mt-16 bg-gray-100 p-8 rounded-lg" initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} data-id="element-155">
          <p className="text-xl text-gray-700 italic mb-4" data-id="element-156">
            "Exceptional growth leader… customer-centric, unwavering focus on
            results."
          </p>
          <p className="text-gray-800 font-bold" data-id="element-157">
            — Kenneth Wong, Director of Engineering
          </p>
        </motion.div>
      </div>
    </div>;
};
export default AffirmPage;